import React, { useCallback, useEffect, useRef } from "react"
import { Box, Typography } from "@mui/material"
import { useStyles } from "./styles"
import { BreadcrumbItem, BreadcrumbsDetailsDataType } from "../../types"
import CustomLink from "../../../CustomLink"
import { FaIcon } from "../../../../../../mp-core/icons"

type Props = {
  breadcrumbs: BreadcrumbItem[]
  hideHomeIcon?: boolean
  clickableLastItem?: boolean
  className?: string
  // When provided, focus is returned to this input on root-nav and on drill-deeper.
  // Omit when breadcrumbs render outside a search context (e.g. inside an option row).
  inputRef?: React.RefObject<HTMLInputElement>
  onBreadcrumbClick?: (
    level: number,
    id?: string,
    details?: BreadcrumbsDetailsDataType
  ) => void
}

const IndentationDropdownBreadcrumbs: React.FC<Props> = ({
  breadcrumbs,
  hideHomeIcon = false,
  clickableLastItem = false,
  className,
  inputRef,
  onBreadcrumbClick
}) => {
  const { classes, cx } = useStyles()
  const prevBreadcrumbsLength = useRef<number>(0)

  const handleBreadcrumbKeyDown = (
    event: React.KeyboardEvent,
    action: () => void
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      // a11y: don't let Enter bubble to Autocomplete and select the highlighted option.
      event.preventDefault()
      event.stopPropagation()
      action()
    }
  }

  // a11y: defer a frame so the state-driven rerender doesn't blow focus away.
  const focusSearchInput = useCallback(() => {
    if (!inputRef) return
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [inputRef])

  const navigateToRoot = () => {
    focusSearchInput()
    onBreadcrumbClick?.(-1)
  }

  // a11y: drilling deeper keeps focus on the search input, not the new crumb.
  useEffect(() => {
    const prev = prevBreadcrumbsLength.current
    const curr = breadcrumbs.length
    prevBreadcrumbsLength.current = curr
    if (curr > prev) focusSearchInput()
  }, [breadcrumbs.length, focusSearchInput])

  return (
    <Box
      component="ol"
      className={cx(classes.breadcrumbsContainer, className)}
      aria-label="Filter path"
    >
      {!hideHomeIcon && (
        <Box component="li" className={classes.breadcrumbListItem}>
          <CustomLink
            role="link"
            tabIndex={0}
            onClick={navigateToRoot}
            onKeyDown={(event: React.KeyboardEvent) =>
              handleBreadcrumbKeyDown(event, navigateToRoot)
            }
            className={classes.breadcrumbItem}
            aria-label="Go to root"
          >
            <FaIcon name="house" solid size="regular" aria-hidden />
          </CustomLink>
        </Box>
      )}

      {breadcrumbs.map((item, index, arr) => {
        const isLast = arr.length - 1 === index
        const isCurrent = isLast && !clickableLastItem
        const navigate = () => onBreadcrumbClick?.(index, item.id, item.details)

        return (
          <Box
            key={item.id}
            component="li"
            className={classes.breadcrumbListItem}
          >
            {(!hideHomeIcon || index !== 0) && (
              <Typography variant="subtitle2" component="span" aria-hidden>
                {">"}
              </Typography>
            )}

            {isCurrent ? (
              // a11y: current crumb is a non-link <span>, not an <a>, so
              // SRs announce "label, current location" instead of "label
              // link, current location" (APG Breadcrumb pattern).
              <Typography
                component="span"
                variant="subtitle2"
                className={classes.breadcrumbItem}
                aria-current="location"
              >
                {item.label}
              </Typography>
            ) : (
              <CustomLink
                role="link"
                variant="subtitle2"
                className={classes.breadcrumbItem}
                tabIndex={0}
                onClick={navigate}
                onKeyDown={(event: React.KeyboardEvent) =>
                  handleBreadcrumbKeyDown(event, navigate)
                }
              >
                {item.label}
              </CustomLink>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default IndentationDropdownBreadcrumbs
